import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  canUserPurchase,
  createLoginRedirectUrl,
  getRoleName,
} from "../utils/authUtils";
import toast from "react-hot-toast";

/**
 * PurchaseProtection component - Wraps purchase actions to ensure only customers can purchase
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components (typically buttons)
 * @param {Function} props.onPurchaseAction - Function to call when purchase action is triggered
 * @param {string} props.fallbackText - Text to show when user cannot purchase
 * @param {string} props.productName - Name of the product for messaging
 * @param {boolean} props.showAsButton - Whether to show as button or link style
 */
const PurchaseProtection = ({
  children,
  onPurchaseAction,
  fallbackText = "Login to Purchase",
  productName = "product",
  showAsButton = true,
}) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleAction = (e) => {
    e.preventDefault();

    // Check if user is authenticated
    if (!isAuthenticated) {
      toast.error(`Please login to purchase ${productName}`);
      const loginUrl = createLoginRedirectUrl(
        location.pathname + location.search
      );
      navigate(loginUrl);
      return;
    }

    // Check if user can make purchases (only customers)
    if (!canUserPurchase(user)) {
      const userRole = getRoleName(user.role);
      toast.error(
        `${userRole} accounts cannot make purchases. Only customer accounts can purchase products.`
      );
      return;
    }

    // User is authenticated and authorized, proceed with purchase action
    if (onPurchaseAction) {
      onPurchaseAction();
    }
  };

  // If user is authenticated and can purchase, return original children
  if (isAuthenticated && canUserPurchase(user)) {
    return children;
  }

  // Otherwise, return protected button/link
  const className = showAsButton
    ? "w-full btn-primary btn-md mr-2"
    : "text-primary-600 hover:text-primary-700 font-medium";

  return (
    <button onClick={handleAction} className={className} type="button">
      {isAuthenticated
        ? `${getRoleName(user.role)} - Cannot Purchase`
        : fallbackText}
    </button>
  );
};

export default PurchaseProtection;
